import React, { useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { fetchAdvertiserReport } from '../services/business/businessService.js';
import '../styles/business.css';

function formatInt(value) {
  return Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function money(value) {
  const number = Number(value || 0);
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
  });
}

function AdvertisingNav() {
  return (
    <nav className="business-subnav" aria-label="WatchVIM advertising">
      <NavLink to="/advertise">Overview</NavLink>
      <NavLink to="/advertise/video">Video Ads</NavLink>
      <NavLink to="/advertise/display">Display Ads</NavLink>
      <NavLink to="/advertise/pricing">Pricing</NavLink>
      <NavLink to="/advertise/dashboard">Campaign Report</NavLink>
      <NavLink to="/advertise/sponsorships">Sponsorships</NavLink>
    </nav>
  );
}

function Metric({ label, value, note }) {
  return (
    <article className="business-card">
      <span className="business-kicker">{label}</span>
      <h2>{value}</h2>
      {note ? <p className="business-muted">{note}</p> : null}
    </article>
  );
}

export function AdvertiserCampaignDashboardPage() {
  const [params, setParams] = useSearchParams();
  const [tokenInput, setTokenInput] = useState(params.get('token') || '');
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  async function load(token = tokenInput) {
    const clean = String(token || '').trim();
    if (!clean) {
      setError('Enter the private reporting token from your WatchVIM campaign confirmation.');
      return;
    }
    setError('');
    setStatus('Loading campaign delivery…');
    setReport(null);
    try {
      const result = await fetchAdvertiserReport(clean);
      setReport(result.report || null);
      setStatus('');
      setParams({ token: clean }, { replace: true });
    } catch (err) {
      setStatus('');
      setError(err?.message || 'Campaign report could not be loaded.');
    }
  }

  useEffect(() => {
    const initial = params.get('token');
    if (initial) load(initial);
    // Auto-load only the token present when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisplay = report?.campaignFormat === 'display';
  const campaignLabel = isDisplay
    ? 'Display image campaign'
    : `${Number(report?.durationSeconds || 0)}-second video campaign`;

  return (
    <section className="business-page advertise-page">
      <header className="business-hero">
        <span className="eyebrow">WatchVIM for Brands</span>
        <h1>Campaign Performance</h1>
        <p>Track purchased impressions, delivery progress, clicks, and campaign activity. Video campaigns also include playback completion metrics.</p>
      </header>
      <div className="business-content">
        <AdvertisingNav />
        <div className="business-form wide">
          <label>Private campaign reporting token<input value={tokenInput} onChange={(event) => setTokenInput(event.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></label>
          <button className="business-primary button" type="button" onClick={() => load()}>Load Campaign Report</button>
          {error ? <p className="business-message error" role="alert">{error}</p> : null}
          {status ? <p className="business-message" role="status">{status}</p> : null}
        </div>

        {report ? (
          <>
            <div className="business-card">
              <span className="business-kicker">{report.companyName}</span>
              <h2>{campaignLabel} · {String(report.status || '').toUpperCase()}</h2>
              <p>{report.billingMode === 'daily' ? `${money(report.dailyBudget)}/day · ${report.flightDays} days` : `${String(report.packageKey || 'flat')} impression package`} · {report.placementScope === 'title' ? (report.targetTitleName || 'title-targeted') : 'platform-wide'}</p>
            </div>

            <div className="business-card-grid four">
              <Metric label="Delivered" value={formatInt(report.impressionsDelivered)} note={`${Number(report.deliveryPercent || 0).toFixed(1)}% of ${formatInt(report.impressionGoal)} purchased`} />
              <Metric label="Remaining" value={formatInt(report.impressionsRemaining)} note="Campaign stops automatically at its purchased goal" />
              {isDisplay ? (
                <Metric label="Clicks" value={formatInt(report.totals?.clicks)} note={`${Number(report.clickThroughRate || 0).toFixed(2)}% CTR`} />
              ) : (
                <Metric label="Completion rate" value={`${Number(report.completionRate || 0).toFixed(1)}%`} note={`${formatInt(report.totals?.completes)} completed video ads`} />
              )}
              {isDisplay ? (
                <Metric label="CTR" value={`${Number(report.clickThroughRate || 0).toFixed(2)}%`} note={`${formatInt(report.totals?.clicks)} tracked clicks`} />
              ) : (
                <Metric label="Clicks" value={formatInt(report.totals?.clicks)} note={`${Number(report.clickThroughRate || 0).toFixed(2)}% CTR`} />
              )}
            </div>

            <div className="business-card-grid three">
              {!isDisplay ? <Metric label="Starts" value={formatInt(report.totals?.starts)} /> : <Metric label="Campaign type" value="Display" note="Image/companion inventory" />}
              <Metric label="Total budget" value={money(report.totalBudget)} note={`${money(report.cpm)} CPM`} />
              <Metric label="Today's delivery" value={formatInt(report.currentDailyImpressions)} note={report.dailyImpressionCap ? `${formatInt(report.dailyImpressionCap)} daily cap` : 'No daily cap'} />
            </div>

            <div className="business-card">
              <h2>Daily Delivery</h2>
              {(report.daily || []).length ? (
                <div className="business-table-wrap">
                  <table className="business-table">
                    <thead>
                      {isDisplay
                        ? <tr><th>Date</th><th>Impressions</th><th>Clicks</th><th>CTR</th></tr>
                        : <tr><th>Date</th><th>Impressions</th><th>Starts</th><th>Completes</th><th>Clicks</th></tr>}
                    </thead>
                    <tbody>
                      {report.daily.map((row) => {
                        const rowCtr = Number(row.impressions || 0) > 0 ? (Number(row.clicks || 0) / Number(row.impressions || 0)) * 100 : 0;
                        return isDisplay ? (
                          <tr key={row.date}><td>{row.date}</td><td>{formatInt(row.impressions)}</td><td>{formatInt(row.clicks)}</td><td>{rowCtr.toFixed(2)}%</td></tr>
                        ) : (
                          <tr key={row.date}><td>{row.date}</td><td>{formatInt(row.impressions)}</td><td>{formatInt(row.starts)}</td><td>{formatInt(row.completes)}</td><td>{formatInt(row.clicks)}</td></tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : <p className="business-muted">Delivery data will appear after the campaign is approved and begins serving.</p>}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
